import TagSelect from '../../components/Tags/TagSelect';
/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Dropdown from '../../components/Dropdown';
import SectionButtonGroups from '../../components/ButtonGroups/SectionButtonGroups';
import DatePicker from '../../components/DatePicker/Picker';
import OperationsTab from '../../components/OperationsTab';
import {
  addToQueryString,
  arrayToQueryString,
  defaultPickerDate,
  getParamFromQuery
} from '../../utils';
import ClearButton from '../../components/ClearButton';
import CalculationTab from '../../components/CalculationTab';
import ViewTypeTab from '../../components/ViewTypeTab';
import { calculations, operations, VIEW_TYPE_OPTIONS } from '../../constants';
import { sendAnalyticsSessionLog } from '../../utils/analytics';

const SUMMARY_CATEGORY_LABELS = {
  overview: 'shown in the overview',
  total: 'shown in the overview',
  authors: 'broken down by developer',
  repos: 'broken down by repository',
  projects: 'broken down by project',
  categories: 'broken down by work category',
  tags: 'broken down by tag',
  branches: 'broken down by branch',
  ai_adoption: 'split by AI leverage',
  retention: 'shown as contribution retention',
  developer_report: 'inside this developer report',
  repo_report: 'inside this repository report',
  project_report: 'inside this project report'
};

const METRIC_LABELS = {
  [operations.COMMITS]: 'commit count',
  [operations.GIT_EFFORT]: 'REV',
  [operations.INVESTMENT]: 'estimated cost without AI'
};

const VIEW_TYPE_SUMMARY = {
  [VIEW_TYPE_OPTIONS.AI]: 'using effort saved by AI only',
  [VIEW_TYPE_OPTIONS.DEVELOPER]: 'using remaining human effort only'
};

const CATEGORY_BLOCKED_VIEW_TYPES = [
  VIEW_TYPE_OPTIONS.AI,
  VIEW_TYPE_OPTIONS.DEVELOPER
];

const PERFORMANCE_SIDEBAR_SUMMARY_PATHS = new Set([
  '/performance-overview',
  '/developers',
  '/repos',
  '/categories',
  '/projects',
  '/tags',
  '/ai-adoption'
]);

const getSelectedItems = value => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'object') return [value];
  return [];
};

const getItemLabel = item => item?.label || item?.value || item;

const formatList = items => {
  const labels = items.map(getItemLabel).filter(Boolean);

  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;

  return `${labels.slice(0, 2).join(', ')}, and ${labels.length - 2} others`;
};

const renderHighlightedValue = value => (
  <span className="font-semibold text-slate-950 dark:text-white">{value}</span>
);

const hasSelectedItems = value => getSelectedItems(value).length > 0;

const isDateRangeChanged = dateRange => {
  if (!dateRange?.startDate || !dateRange?.endDate) return false;

  const defaultDateRange = defaultPickerDate().dateRange;

  return (
    dateRange.startDate !== defaultDateRange.startDate ||
    dateRange.endDate !== defaultDateRange.endDate
  );
};

const getContributorPhrase = filters => {
  const authors = getSelectedItems(filters.authors);
  if (authors.length) {
    return (
      <>
        {' by '}
        {renderHighlightedValue(formatList(authors))}
      </>
    );
  }

  const abilities = getSelectedItems(filters.language_tags);
  const tags = getSelectedItems(filters.tags);
  const developerQualifiers = [];

  if (abilities.length) {
    developerQualifiers.push(
      `with ${formatList(abilities)} ${abilities.length === 1 ? 'ability' : 'abilities'}`
    );
  }

  if (tags.length) {
    developerQualifiers.push(`tagged ${formatList(tags)}`);
  }

  if (developerQualifiers.length) {
    return (
      <>
        {' by developers '}
        {renderHighlightedValue(developerQualifiers.join(' and '))}
      </>
    );
  }

  return null;
};

const getScopePhrase = filters => {
  const repos = getSelectedItems(filters.repos);
  if (repos.length) {
    if (repos.length === 1) {
      return (
        <>
          {' in the '}
          {renderHighlightedValue(formatList(repos))}
          {' repository'}
        </>
      );
    }

    return (
      <>
        {' in '}
        {renderHighlightedValue(formatList(repos))}
        {' repositories'}
      </>
    );
  }

  const projects = getSelectedItems(filters.projects);
  if (projects.length) {
    if (projects.length === 1) {
      return (
        <>
          {' in the '}
          {renderHighlightedValue(formatList(projects))}
          {' project'}
        </>
      );
    }

    return (
      <>
        {' in '}
        {renderHighlightedValue(formatList(projects))}
        {' projects'}
      </>
    );
  }

  const branches = getSelectedItems(filters.branches);
  if (branches.length) {
    if (branches.length === 1) {
      return (
        <>
          {' on the '}
          {renderHighlightedValue(formatList(branches))}
          {' branch'}
        </>
      );
    }

    return (
      <>
        {' on '}
        {renderHighlightedValue(formatList(branches))}
        {' branches'}
      </>
    );
  }

  return ' across all repositories';
};

const getWorkPhrase = filters => {
  const categories = getSelectedItems(filters.categories);

  if (categories.length) {
    return (
      <>
        {renderHighlightedValue(formatList(categories))}
        {' work'}
      </>
    );
  }

  return 'overall work';
};

const hasActiveSummaryFilter = (filters, metric) => {
  const hasEntityFilter = [
    'projects',
    'repos',
    'branches',
    'authors',
    'categories',
    'tags',
    'language_tags'
  ].some(key => hasSelectedItems(filters[key]));

  return (
    hasEntityFilter ||
    metric !== operations.GIT_EFFORT ||
    filters.viewType === VIEW_TYPE_OPTIONS.AI ||
    filters.viewType === VIEW_TYPE_OPTIONS.DEVELOPER ||
    filters.buttonGroup === 'Developers' ||
    filters.buttonGroup === 'Repos' ||
    filters.buttonGroup === 'Project' ||
    filters.calculationsMethod === calculations.COUNT ||
    isDateRangeChanged(filters.dateRange)
  );
};

const buildFilterSummary = ({ filters, summaryCategory, summaryMetric }) => {
  const metric = summaryMetric ?? filters.operation ?? operations.GIT_EFFORT;

  if (!hasActiveSummaryFilter(filters, metric)) return '';

  const metricLabel = METRIC_LABELS[metric] || metric;
  const workPhrase = getWorkPhrase(filters);
  const contributorPhrase = getContributorPhrase(filters);
  const scopePhrase = getScopePhrase(filters);
  const categoryPhrase =
    SUMMARY_CATEGORY_LABELS[summaryCategory] || 'shown in this dashboard';
  const viewTypePhrase = VIEW_TYPE_SUMMARY[filters.viewType]
    ? renderHighlightedValue(VIEW_TYPE_SUMMARY[filters.viewType])
    : null;
  const datePhrase =
    filters.dateRange?.startDate && filters.dateRange?.endDate ? (
      <>
        {'from '}
        {renderHighlightedValue(
          `${filters.dateRange.startDate} to ${filters.dateRange.endDate}`
        )}
      </>
    ) : (
      ''
    );
  const summaryParts = [categoryPhrase, viewTypePhrase, datePhrase].filter(
    Boolean
  );

  return (
    <>
      {"You're viewing "}
      {renderHighlightedValue(metricLabel)}
      {' for '}
      {workPhrase}
      {contributorPhrase}
      {scopePhrase}
      {summaryParts.length > 0 &&
        summaryParts.map((part, index) => (
          <React.Fragment key={`summary-part-${index}`}>
            {index === 0 ? ', ' : ', '}
            {part}
          </React.Fragment>
        ))}
      .
    </>
  );
};

export const Filters = ({
  userInfo,
  isNotMulti = [],
  onChange,
  isHidden = [],
  isVisibleLangugageTags,
  retentionCategoriesVisible = false,
  operationKeys,
  afterViewType,
  showContextSummary = false,
  summaryCategory,
  summaryMetric,
  forceClearMultiFilters = [],
  allowRepoProjectIntersection = false
}) => {
  const { pathname } = useLocation();
  const [projectsOptions, setProjectsOptions] = useState([]);
  const [reposOptions, setReposOptions] = useState([]);
  const [languageTags, setLanguageTags] = useState([]);

  // const [branchesOptions, setBranchesOptions] = useState([]);
  const [authorsOptions, setAuthorsOptions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [inVisibleFilters, setInVisibleFilters] = useState(isHidden);
  const [buttonGroup, setButtonGroup] = useState('Overview');

  const [tags, setTags] = useState([]);

  const [valuesOptions, setValuesOptions] = useState(defaultPickerDate);

  const filterDropdownValue = (
    name,
    includedKeys = [],
    keyCheck,
    tempValue
  ) => {
    const tempData = userInfo?.dashboardData?.[name];
    if (!tempData) return [];

    const filteredData = Object.keys(tempData)
      .filter(key => (keyCheck ? includedKeys.includes(key) : true))
      .reduce((acc, key) => {
        acc[key] = tempData[key];
        return acc;
      }, {});

    if (keyCheck && tempValue?.[name]) {
      const allowedFilter = Object.keys(filteredData);

      const valuesArray = tempValue[name].filter(item =>
        allowedFilter.includes(item.value)
      );
      tempValue[name] = valuesArray;
    }

    return Object.values(filteredData);
  };
  const getTagsDropdownData = (tagsData, tempValue, isArray) => {
    const filterDetails = userInfo.dashboardData.authors;
    const normalizedTagsData = Array.isArray(tagsData)
      ? tagsData
      : tagsData
        ? [tagsData]
        : [];
    const result = !isArray
      ? normalizedTagsData.map(item => item?.value ?? item).filter(Boolean)
      : normalizedTagsData;

    const authorsArray = Object.values(filterDetails);

    const authorsData = authorsArray.filter(author => {
      if (result.length === 0) return true;

      if (Array.isArray(author.tags)) {
        const flatTags = author.tags.flat();
        return flatTags.some(tag => result.includes(tag));
      }

      return false;
    });

    const authorsUids = authorsData.map(filt => filt.value);

    setAuthorsOptions(
      filterDropdownValue('authors', authorsUids, true, tempValue)
    );
  };

  const onChangeHandle = (key, e) => {
    const tempValue = { ...valuesOptions, [key]: e };
    const hasSelectedCategories =
      key === 'categories'
        ? getSelectedItems(e).length > 0
        : getSelectedItems(tempValue.categories).length > 0;

    if (
      !allowRepoProjectIntersection &&
      key === 'repos' &&
      getSelectedItems(e).length > 0
    ) {
      tempValue.projects = [];
      arrayToQueryString([], 'projects');
    }

    if (
      (key === 'categories' && hasSelectedCategories) ||
      (key === 'viewType' &&
        hasSelectedCategories &&
        CATEGORY_BLOCKED_VIEW_TYPES.includes(e))
    ) {
      tempValue.viewType = VIEW_TYPE_OPTIONS.TOTAL;
      addToQueryString(VIEW_TYPE_OPTIONS.TOTAL, 'viewType');
    }

    if (key === 'tags') {
      arrayToQueryString(e || [], 'tags');
      getTagsDropdownData(e, tempValue);
    }

    forceClearMultiFilters.forEach(filterKey => {
      tempValue[filterKey] = [];
      arrayToQueryString([], filterKey);
    });

    setValuesOptions(tempValue);
    onChange(tempValue);

    const normalizeFilterValue = value => {
      if (Array.isArray(value)) {
        return value.map(item => item?.value ?? item);
      }

      if (value && typeof value === 'object') {
        if ('startDate' in value || 'endDate' in value) {
          return {
            startDate: value.startDate,
            endDate: value.endDate
          };
        }

        return value?.value ?? null;
      }

      return value ?? null;
    };

    sendAnalyticsSessionLog({
      eventName: 'filter_change',
      payload: {
        filter_key: key,
        filter_value: normalizeFilterValue(e)
      }
    });
  };

  const changeButtonGroup = (e, firstLoad) => {
    setButtonGroup(e);
    const array = [
      'projects',
      'repos',
      'tags',
      'branches',
      'authors',
      'categories',
      'operation',
      'viewType',
      'clearButton'
    ];

    let itemToRemove;
    if (e === 'Developers') {
      itemToRemove = 'authors';
    }

    if (e === 'Repos') {
      itemToRemove = 'repos';
    }

    if (e === 'Project') {
      itemToRemove = 'projects';
    }

    if (e === 'Tags') {
      itemToRemove = 'tags';
    }

    const updatedArray = array.filter(item => item !== itemToRemove);
    if (!firstLoad) {
      const filteredObj = Object.fromEntries(
        Object.entries(valuesOptions).filter(
          ([key]) => !updatedArray.includes(key)
        )
      );
      filteredObj.buttonGroup = e;
      if (filteredObj[itemToRemove]) {
        onChange({ ...filteredObj });
      }

      setValuesOptions({ ...filteredObj });
      if (e === 'Overview') {
        onChange({ ...filteredObj });
      }
    }

    addToQueryString(e, 'buttonGroup');
    setInVisibleFilters(updatedArray);

    if (!firstLoad) {
      sendAnalyticsSessionLog({
        eventName: 'filter_change',
        payload: {
          filter_key: 'buttonGroup',
          filter_value: e
        }
      });
    }
  };

  useEffect(() => {
    if (userInfo.dashboardData?.projects) {
      setProjectsOptions(filterDropdownValue('projects'));
      setReposOptions(filterDropdownValue('repos'));
      setLanguageTags(filterDropdownValue('language_tags'));
      //  setBranchesOptions(filterDropdownValue('branches'));

      const shouldHydrateTagsFromQuery =
        !forceClearMultiFilters.includes('tags') &&
        getParamFromQuery('tags') &&
        getParamFromQuery('tags').length > 0;

      if (shouldHydrateTagsFromQuery) {
        const tempValue = valuesOptions;

        getTagsDropdownData(getParamFromQuery('tags'), tempValue, true);
        setValuesOptions(tempValue);
      } else {
        setAuthorsOptions(filterDropdownValue('authors'));
      }
      setCategories(filterDropdownValue('categories'));
      setTags(filterDropdownValue('tags'));
    }
  }, [userInfo.dashboardData]);

  const queryStringTemp = (tempValue, name) => {
    if (forceClearMultiFilters.includes(name)) return tempValue;

    const reposArray = getParamFromQuery(name);
    if (reposArray !== null) {
      if (Array.isArray(reposArray)) {
        reposArray.forEach((element, index) => {
          if (!tempValue[name]) tempValue[name] = [];
          if (isNotMulti.indexOf(name) !== -1) {
            tempValue[name] = {
              ...userInfo.dashboardData?.[name]?.[element]
            };
          } else {
            tempValue[name][index] = {
              ...userInfo.dashboardData?.[name]?.[element]
            };
          }
        });
      } else {
        tempValue[name] = reposArray;
      }
    }

    return tempValue;
  };

  useEffect(() => {
    if (valuesOptions?.dateRange) {
      let tempValue = { ...valuesOptions };
      tempValue = queryStringTemp(tempValue, 'repos');
      tempValue = queryStringTemp(tempValue, 'projects');
      tempValue = queryStringTemp(tempValue, 'branches');
      tempValue = queryStringTemp(tempValue, 'authors');
      tempValue = queryStringTemp(tempValue, 'categories');
      tempValue = queryStringTemp(tempValue, 'operation');
      tempValue = queryStringTemp(tempValue, 'viewType');
      tempValue = queryStringTemp(tempValue, 'calculationsMethod');
      tempValue = queryStringTemp(tempValue, 'tags');
      tempValue = queryStringTemp(tempValue, 'language_tags');

      forceClearMultiFilters.forEach(filterKey => {
        tempValue[filterKey] = [];
        arrayToQueryString([], filterKey);
      });

      if (
        getSelectedItems(tempValue.categories).length > 0 &&
        CATEGORY_BLOCKED_VIEW_TYPES.includes(tempValue.viewType)
      ) {
        tempValue.viewType = VIEW_TYPE_OPTIONS.TOTAL;
        addToQueryString(VIEW_TYPE_OPTIONS.TOTAL, 'viewType');
      }

      const dateQueryStartDate = getParamFromQuery('startDate');
      const dateQueryEndDate = getParamFromQuery('endDate');
      let rangeTemp = valuesOptions?.dateRange;
      if (dateQueryStartDate) {
        rangeTemp = {
          startDate: dateQueryStartDate,
          endDate: dateQueryEndDate || dateQueryStartDate
        };
      }

      tempValue['dateRange'] = rangeTemp;

      if (retentionCategoriesVisible) {
        const reposArray = getParamFromQuery('buttonGroup');
        tempValue.buttonGroup = reposArray;

        changeButtonGroup(reposArray || 'Overview', true);
      }

      setValuesOptions(tempValue);
      onChange({ ...tempValue });
    }
  }, []);

  const shouldShowContextSummary =
    showContextSummary && PERFORMANCE_SIDEBAR_SUMMARY_PATHS.has(pathname);

  const contextSummary = shouldShowContextSummary
    ? buildFilterSummary({
        filters: valuesOptions,
        summaryCategory,
        summaryMetric
      })
    : '';

  return (
    <>
      {inVisibleFilters.indexOf('dateRange') === -1 && (
        <DatePicker
          dateRange={valuesOptions?.dateRange}
          onChange={e => {
            onChangeHandle('dateRange', e);
          }}
        />
      )}
      {retentionCategoriesVisible && (
        <>
          <SectionButtonGroups
            items={['Overview', 'Developers', 'Repos', 'Project', 'Tags']}
            buttonGroup={buttonGroup}
            onChange={e => {
              changeButtonGroup(e);
            }}
          />
        </>
      )}

      {inVisibleFilters.indexOf('projects') === -1 && (
        <Dropdown
          options={projectsOptions}
          isMulti={isNotMulti.indexOf('projects') === -1}
          isDisabled={
            !allowRepoProjectIntersection &&
            getSelectedItems(valuesOptions?.repos).length > 0
          }
          selectedValue={valuesOptions?.projects}
          label={'projects'}
          onChange={e => {
            onChangeHandle('projects', e);
          }}
        />
      )}

      {inVisibleFilters.indexOf('repos') === -1 && (
        <Dropdown
          options={reposOptions}
          isMulti={isNotMulti.indexOf('repos') === -1}
          selectedValue={valuesOptions?.repos}
          label={'repos'}
          onChange={e => {
            onChangeHandle('repos', e);
          }}
        />
      )}
      {/* 
      {inVisibleFilters.indexOf('branches') === -1 && (
        <Dropdown
          options={branchesOptions}
          isMulti={isNotMulti.indexOf('branches') === -1}
          selectedValue={valuesOptions?.branches}
          label={'branches'}
          onChange={e => {
            onChangeHandle('branches', e);
          }}
        />
      )}
    */}
      {inVisibleFilters.indexOf('authors') === -1 && (
        <>
          <Dropdown
            options={authorsOptions}
            isMulti={isNotMulti.indexOf('authors') === -1 ? true : false}
            label={'authors'}
            selectedValue={valuesOptions?.authors}
            onChange={e => {
              onChangeHandle('authors', e);
            }}
          />
        </>
      )}
      {inVisibleFilters.indexOf('categories') === -1 && (
        <Dropdown
          options={categories}
          isMulti={isNotMulti.indexOf('categories') === -1}
          label={'categories'}
          isDisabled={
            valuesOptions?.viewType === VIEW_TYPE_OPTIONS.AI ||
            valuesOptions?.viewType === VIEW_TYPE_OPTIONS.DEVELOPER
          }
          selectedValue={valuesOptions?.categories}
          onChange={e => {
            onChangeHandle('categories', e);
          }}
        />
      )}
      {inVisibleFilters.indexOf('tags') === -1 && (
        <TagSelect
          allowGroupSelection
          groups={userInfo?.dashboardData?.tag_groups}
          options={tags}
          isMulti={isNotMulti.indexOf('tags') === -1}
          label="Tags"
          inputId="tags-filter"
          aria-label="Tags"
          selectedValue={valuesOptions?.tags}
          onChange={e => {
            onChangeHandle('tags', e);
          }}
        />
      )}
      {inVisibleFilters.indexOf('language_tags') === -1 &&
        isVisibleLangugageTags && (
          <Dropdown
            options={languageTags}
            isMulti={isNotMulti.indexOf('language_tags') === -1}
            selectedValue={valuesOptions?.language_tags}
            label={'Abilities'}
            onChange={e => {
              onChangeHandle('language_tags', e);
            }}
          />
        )}

      <div className="flex gap-2">
        {retentionCategoriesVisible && false && (
          <CalculationTab
            activeValue={valuesOptions?.calculationsMethod}
            onChange={e => {
              onChangeHandle('calculationsMethod', e);
            }}
          />
        )}
        {inVisibleFilters.indexOf('operation') === -1 && (
          <OperationsTab
            activeValue={valuesOptions?.operation}
            operationKeys={operationKeys}
            onChange={e => {
              onChangeHandle('operation', e);
            }}
          />
        )}
        {inVisibleFilters.indexOf('viewType') === -1 && (
          <ViewTypeTab
            activeValue={valuesOptions?.viewType}
            disabledValues={
              getSelectedItems(valuesOptions?.categories).length > 0
                ? CATEGORY_BLOCKED_VIEW_TYPES
                : []
            }
            onChange={e => {
              onChangeHandle('viewType', e);
            }}
          />
        )}
        {afterViewType}
        {inVisibleFilters.indexOf('clearButton') === -1 && <ClearButton />}
      </div>
      {contextSummary && (
        <p className="w-full rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium leading-5 text-slate-800 shadow-sm dark:border-blue-800/70 dark:bg-blue-950/40 dark:text-slate-100">
          {contextSummary}
        </p>
      )}
    </>
  );
};
